import React, {useCallback, useEffect, useRef, useState} from "react";
import * as PIXI from "pixi.js";
import socket from "../utils/socketClient";
import {apiGet} from "../api/apiClient";
import {initMap} from "../pixi/mapRenderer";
import {initObjects} from "../pixi/objectHandler";
import {
    AVATAR_SHEET_DIRECTIONS,
    AVATAR_SHEET_FRAME_HEIGHT,
    AVATAR_SHEET_FRAME_WIDTH,
    AVATAR_SHEET_FRAMES_PER_DIRECTION,
    AVATAR_SHEET_RENDER_TILE_WIDTH,
    avatarFallbackSrc,
    avatarSpriteSheetSrc,
} from "../utils/avatarAssets";
// Controls how high the name label sits above the avatar.
// Increase these values to add more space.
const NAME_OFFSET_MULT = 1.15;   // multiplier of sprite height
const NAME_OFFSET_PX = 1;        // extra pixels
// import { initAvatars } from "../pixi/avatarHandler";

const DEFAULT_ROOM_NAME = "room1.1_lobby";
const LEGACY_DEFAULT_ROOM_NAME = "room1";
const LOBBY_SPAWN_Y_MIN = 320;
const LOBBY_SPAWN_Y_MAX = 348;
const LAB_ROOM_NAME = "room1.2_lab";
const LAB_SPAWN_Y = 768;
const DISCUSSION_ROOM_NAME = "room1.3_discussion";
const DISCUSSION_SPAWN_Y = 896;
const COMPETITION_ROOM_NAME = "room1.4_competition";
const COMPETITION_SPAWN_Y = 768;
const MAP_VISUAL_SCALE = 2;
const LAB_VISUAL_SCALE = 1.6;

const ROOM_SPAWN_AREAS = {
    [DEFAULT_ROOM_NAME]: {minY: LOBBY_SPAWN_Y_MIN, maxY: LOBBY_SPAWN_Y_MAX},
    [LAB_ROOM_NAME]: {minY: LAB_SPAWN_Y, maxY: LAB_SPAWN_Y},
    [DISCUSSION_ROOM_NAME]: {minX: 32, maxX: 800, minY: DISCUSSION_SPAWN_Y, maxY: DISCUSSION_SPAWN_Y},
    [COMPETITION_ROOM_NAME]: {minX: 32, maxX: 800, minY: COMPETITION_SPAWN_Y, maxY: COMPETITION_SPAWN_Y},
};

const randomInt = (min, max) => {
    const safeMin = Math.ceil(Math.min(min, max));
    const safeMax = Math.floor(Math.max(min, max));
    return Math.floor(Math.random() * (safeMax - safeMin + 1)) + safeMin;
};

const normalizedRoomFileBase = (room) => {
    const cleaned = String(room || "").trim().replace(/^\/+/, "");
    return (cleaned.split("/").pop() || "").replace(/\.json$/i, "");
};

const randomSpawnPosition = ({mapWidth = 0, mapHeight = 0, tileSize = 16, room = DEFAULT_ROOM_NAME} = {}) => {
    const normalizedRoom = normalizeRoomName(room);
    const margin = tileSize * 2;
    const safeMapWidth = Math.max(margin * 2, Number(mapWidth) || 0);
    const safeMapHeight = Math.max(margin * 2, Number(mapHeight) || 0);
    const minX = margin;
    const maxX = Math.max(minX, safeMapWidth - margin);
    const maxObjectX = Math.max(minX, safeMapWidth - tileSize);
    const maxObjectY = Math.max(margin, safeMapHeight - tileSize);
    let spawnMinX = minX;
    let spawnMaxX = maxX;
    let minY = margin;
    let maxY = Math.max(minY, safeMapHeight - margin);
    const spawnArea = ROOM_SPAWN_AREAS[normalizedRoom];

    if (spawnArea) {
        spawnMinX = Math.max(minX, Math.min(spawnArea.minX ?? minX, maxObjectX));
        spawnMaxX = Math.max(spawnMinX, Math.min(spawnArea.maxX ?? maxX, maxObjectX));
        minY = Math.max(margin, Math.min(spawnArea.minY ?? minY, maxObjectY));
        maxY = Math.max(minY, Math.min(spawnArea.maxY ?? maxY, maxObjectY));
    }

    return {
        x: randomInt(spawnMinX, spawnMaxX),
        y: randomInt(minY, maxY),
    };
};

const isPositionInsideMap = (position, mapWidth, mapHeight, tileSize) => {
    if (!position) return false;
    const x = Number(position.x);
    const y = Number(position.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
    const margin = tileSize * 2;
    return x >= margin && y >= margin && x <= mapWidth - tileSize && y <= mapHeight - tileSize;
};

const clearGlobalMovementKeys = () => {
    if (!window.__gs_keys) return;
    window.__gs_keys.ArrowUp = false;
    window.__gs_keys.ArrowDown = false;
    window.__gs_keys.ArrowLeft = false;
    window.__gs_keys.ArrowRight = false;
};

const avatarPositionStorageKey = (user, suffix = "position") => {
    const userKey = user?.user_id || user?.id || user?.email || "guest";
    const courseKey = user?.course_id || "course";
    return `gamifyit:avatar:${courseKey}:${userKey}:${suffix}`;
};

const readStoredAvatarPosition = (user, suffix = "position") => {
    try {
        const raw = localStorage.getItem(avatarPositionStorageKey(user, suffix));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const x = Number(parsed.x);
        const y = Number(parsed.y);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
        const storedRoom = normalizedRoomFileBase(parsed.room || DEFAULT_ROOM_NAME);
        return {
            x,
            y,
            room: normalizeRoomName(parsed.room || DEFAULT_ROOM_NAME),
            direction: parsed.direction || "right",
            isLegacyDefaultRoom: storedRoom === LEGACY_DEFAULT_ROOM_NAME,
        };
    } catch (error) {
        return null;
    }
};

const saveStoredAvatarPosition = (user, position, suffix = "position") => {
    if (!position) return;
    try {
        localStorage.setItem(avatarPositionStorageKey(user, suffix), JSON.stringify({
            x: Number(position.x),
            y: Number(position.y),
            room: normalizeRoomName(position.room || DEFAULT_ROOM_NAME),
            direction: position.direction || "right",
            saved_at: Date.now(),
        }));
    } catch (error) {
        // Ignore storage failures; avatar can still spawn normally.
    }
};

const clearStoredActivityStartPosition = (user) => {
    try {
        localStorage.removeItem(avatarPositionStorageKey(user, "activity-start"));
    } catch (error) {
        // Ignore storage failures.
    }
};

const avatarAnimationCache = {};

const avatarPublicPath = (user) => {
    if (user.avatar_public_path && user.avatar_public_path.trim() !== "") {
        return user.avatar_public_path;
    }
    return user.avatar && user.avatar.trim() !== "" ? user.avatar : null;
};

const textureFromFrame = (baseTexture, frame) => new PIXI.Texture({
    source: baseTexture.source,
    frame,
});

const buildDirectionalFrames = (texture) => {
    const framesByDirection = {};

    AVATAR_SHEET_DIRECTIONS.forEach((direction, directionIndex) => {
        framesByDirection[direction] = [];

        for (let i = 0; i < AVATAR_SHEET_FRAMES_PER_DIRECTION; i += 1) {
            const frameIndex = directionIndex * AVATAR_SHEET_FRAMES_PER_DIRECTION + i;
            framesByDirection[direction].push(textureFromFrame(
                texture,
                new PIXI.Rectangle(
                    frameIndex * AVATAR_SHEET_FRAME_WIDTH,
                    0,
                    AVATAR_SHEET_FRAME_WIDTH,
                    AVATAR_SHEET_FRAME_HEIGHT
                )
            ));
        }
    });

    return framesByDirection;
};

const loadAvatarAnimation = async (path) => {
    const cacheKey = path || "__default__";
    if (avatarAnimationCache[cacheKey]) return avatarAnimationCache[cacheKey];

    const loadPromise = (async () => {
        if (path) {
            const texture = await PIXI.Assets.load(avatarSpriteSheetSrc(path));
            return {
                frames: buildDirectionalFrames(texture),
                mode: "directional-sheet",
            };
        }

        const texture = await PIXI.Assets.load(avatarFallbackSrc());
        return {
            frames: {right: [texture], up: [texture], left: [texture], down: [texture]},
            mode: "static",
        };
    })();

    avatarAnimationCache[cacheKey] = loadPromise;
    return loadPromise;
};

const setAvatarDirection = (avatar, direction) => {
    const dir = direction || avatar.lastDirection || "right";
    const frames = avatar.framesByDirection?.[dir] || avatar.framesByDirection?.right;
    const sprite = avatar.sprite;

    if (frames && sprite.textures !== frames) {
        const wasPlaying = sprite.playing;
        sprite.textures = frames;
        sprite.gotoAndStop(0);
        if (wasPlaying && sprite.play) sprite.play();
    }

    sprite.scale.x = sprite.baseScaleX;
};

const applyAvatarCounterScale = (display, counterScale) => {
    if (!display || !Number.isFinite(counterScale)) return;
    display.scale.set(counterScale);
};

const normalizeRoomName = (room) => {
    const cleaned = String(room || DEFAULT_ROOM_NAME).trim().replace(/^\/+/, "");
    const fileName = cleaned.split("/").pop() || DEFAULT_ROOM_NAME;
    const normalized = fileName.replace(/\.json$/i, "") || DEFAULT_ROOM_NAME;
    return normalized === LEGACY_DEFAULT_ROOM_NAME ? DEFAULT_ROOM_NAME : normalized;
};

const roomFileName = (room) => `${normalizeRoomName(room)}.json`;

const visualScaleForRoom = (room) =>
    normalizeRoomName(room) === LAB_ROOM_NAME ? LAB_VISUAL_SCALE : MAP_VISUAL_SCALE;

const fetchRoomData = async (room) => {
    const response = await fetch(`/maps/${roomFileName(room)}`);
    if (!response.ok) {
        throw new Error(`Failed to load room data: ${roomFileName(room)} (HTTP ${response.status})`);
    }
    return response.json();
};

const getObjectProperty = (obj, name) =>
    obj.properties?.find((property) => property.name === name)?.value;

const normalizeTableObjectKey = (objectId) => {
    const value = String(objectId || "").trim();
    return value ? value : null;
};

const tableGroupIdsFromRoom = (roomData) => {
    const objectLayer = roomData?.layers?.find(
        (layer) => layer.type === "objectgroup" && layer.name === "interactables"
    );
    if (!objectLayer?.objects) return [];

    return Array.from(new Set(
        objectLayer.objects
            .filter((obj) => {
                const name = String(getObjectProperty(obj, "name") || "").toLowerCase();
                const url = String(getObjectProperty(obj, "url") || "").toLowerCase();
                return name === "table" && url.includes("/table");
            })
            .map((obj) => Number.parseInt(getObjectProperty(obj, "group"), 10))
            .filter((groupId) => Number.isFinite(groupId) && groupId > 0)
    ));
};

const computerObjectIdsFromRoom = (roomData) => {
    const objectLayer = roomData?.layers?.find(
        (layer) => layer.type === "objectgroup" && layer.name === "interactables"
    );
    if (!objectLayer?.objects) return [];

    return Array.from(new Set(
        objectLayer.objects
            .filter((obj) => {
                const name = String(getObjectProperty(obj, "name") || "").toLowerCase();
                const url = String(getObjectProperty(obj, "url") || "").toLowerCase();
                return name === "computer" && url.includes("/individual");
            })
            .map((obj) => normalizeTableObjectKey(getObjectProperty(obj, "id") || obj.id))
            .filter(Boolean)
    ));
};

const buildLiveTableOccupancy = (usersData, currentUser) => {
    const currentUserId = String(currentUser?.user_id || currentUser?.id || "");
    const currentCourseGroupId = currentUser?.course_group_id == null
        ? null
        : String(currentUser.course_group_id);
    const groups = {};

    Object.values(usersData || {}).forEach((presence) => {
        const status = presence?.activity_status;
        if (!isActiveAvatarStatus(status) || status.type !== "group_discussion") return;

        if (
            currentCourseGroupId !== null
            && presence?.course_group_id != null
            && String(presence.course_group_id) !== currentCourseGroupId
        ) {
            return;
        }

        const key = normalizeTableObjectKey(status.object_id);
        if (!key) return;

        if (!groups[key]) {
            groups[key] = {
                group_id: Number.parseInt(status.group_id, 10) || null,
                object_id: status.object_id,
                is_member: false,
                is_occupied: true,
                member_count: 0,
                member_names: [],
                user_ids: [],
                source: "live",
            };
        }

        const userId = String(presence.user_id || presence.id || "");
        groups[key].member_count += 1;
        if (presence.name) groups[key].member_names.push(presence.name);
        if (userId) groups[key].user_ids.push(userId);
        if (userId && currentUserId && userId === currentUserId) {
            groups[key].is_member = true;
            groups[key].is_occupied = false;
        }
    });

    return groups;
};

const INDIVIDUAL_STATUS_TYPES = new Set([
    "individual_exercise",
    "individual_pre_test",
    "individual_post_test",
]);

const buildLiveComputerOccupancy = (usersData, currentUser) => {
    const currentUserId = String(currentUser?.user_id || currentUser?.id || "");
    const computers = {};

    Object.values(usersData || {}).forEach((presence) => {
        const status = presence?.activity_status;
        if (!isActiveAvatarStatus(status) || !INDIVIDUAL_STATUS_TYPES.has(status.type)) return;

        const key = normalizeTableObjectKey(status.object_id);
        if (!key) return;

        const userId = String(presence.user_id || presence.id || "");
        computers[key] = {
            object_id: key,
            user_id: presence.user_id || presence.id || null,
            user_name: presence.name || "",
            is_owner: !!userId && !!currentUserId && userId === currentUserId,
            is_occupied: !(!!userId && !!currentUserId && userId === currentUserId),
            source: "live",
        };
    });

    return computers;
};

const createAvatarNameTag = (name) => {
    const paddingX = 8;
    const paddingY = 4;
    const borderSize = 2;
    const corner = 4;
    const container = new PIXI.Container();
    const label = new PIXI.Text({
        text: name || "User",
        style: {
            fontFamily: "monospace",
            fontSize: 11,
            fill: "#ffffff",
            fontWeight: "900",
            align: "center",
            stroke: {
                color: "#111827",
                width: 2,
            },
        },
        textureStyle: {
            scaleMode: "nearest",
        },
    });

    const width = Math.ceil(label.width + paddingX * 2);
    const height = Math.ceil(label.height + paddingY * 2);
    const shadow = new PIXI.Graphics()
        .rect(corner + 2, 2, width - corner * 2, height)
        .rect(2, corner + 2, width, height - corner * 2)
        .fill(0x111827);
    shadow.alpha = 0.5;

    const background = new PIXI.Graphics()
        .rect(corner, 0, width - corner * 2, height)
        .rect(0, corner, width, height - corner * 2)
        .fill(0x38bdf8)
        .rect(corner + borderSize, borderSize, width - (corner + borderSize) * 2, height - borderSize * 2)
        .rect(borderSize, corner + borderSize, width - borderSize * 2, height - (corner + borderSize) * 2)
        .fill(0x172033);

    const shine = new PIXI.Graphics()
        .rect(corner + borderSize, borderSize, width - (corner + borderSize) * 2, 1)
        .fill(0xffffff);
    shine.alpha = 0.24;

    label.x = width / 2;
    label.y = height / 2;
    label.anchor.set(0.5);
    container.addChild(shadow, background, shine, label);
    container.pivot.set(width / 2, height);
    container.__bubbleHeight = height;
    return container;
};

const createAvatarStatusBubble = (text) => {
    const paddingX = 7;
    const paddingY = 4;
    const borderSize = 2;
    const corner = 4;
    const container = new PIXI.Container();
    const label = new PIXI.Text({
        text,
        style: {
            fontFamily: "monospace",
            fontSize: 10,
            fill: "#fff7ed",
            fontWeight: "900",
            align: "center",
            wordWrap: true,
            wordWrapWidth: 130,
            lineHeight: 14,
            stroke: {
                color: "#111827",
                width: 2,
            },
        },
        textureStyle: {
            scaleMode: "nearest",
        },
    });

    const width = Math.ceil(label.width + paddingX * 2);
    const height = Math.ceil(label.height + paddingY * 2);
    const shadow = new PIXI.Graphics()
        .rect(corner + 2, 2, width - corner * 2, height)
        .rect(2, corner + 2, width, height - corner * 2)
        .fill(0x111827);
    shadow.alpha = 0.48;

    const background = new PIXI.Graphics()
        .rect(corner, 0, width - corner * 2, height)
        .rect(0, corner, width, height - corner * 2)
        .fill(0xf59e0b)
        .rect(corner + borderSize, borderSize, width - (corner + borderSize) * 2, height - borderSize * 2)
        .rect(borderSize, corner + borderSize, width - borderSize * 2, height - (corner + borderSize) * 2)
        .fill(0x3b2a12);

    label.x = width / 2;
    label.y = height / 2;
    label.anchor.set(0.5);
    container.addChild(shadow, background, label);
    container.pivot.set(width / 2, height);
    container.__bubbleHeight = height;
    container.__statusText = text;
    return container;
};

const isActiveAvatarStatus = (status) =>
    !!status?.label && (!status.expires_at || Number(status.expires_at) > Date.now());

const labelRect = (display, padding = 3) => {
    if (!display || display.destroyed) return null;
    const width = Math.max(1, display.width || display.getLocalBounds?.()?.width || 1);
    const height = Math.max(1, display.height || display.getLocalBounds?.()?.height || 1);
    const pivotX = display.pivot?.x || 0;
    const pivotY = display.pivot?.y || 0;
    return {
        x: display.x - pivotX - padding,
        y: display.y - pivotY - padding,
        width: width + padding * 2,
        height: height + padding * 2,
    };
};

const rectsOverlap = (a, b) =>
    !!a && !!b
    && a.x < b.x + b.width
    && a.x + a.width > b.x
    && a.y < b.y + b.height
    && a.y + a.height > b.y;

const positionAvatarLabels = (avatar) => {
    if (!avatar?.sprite) return;
    const nameText = avatar.nameText;
    const statusText = avatar.statusText;

    if (nameText) {
        nameText.x = avatar.sprite.x;
        nameText.y = avatar.sprite.y - avatar.sprite.height * NAME_OFFSET_MULT - NAME_OFFSET_PX;
        nameText.zIndex = avatar.sprite.zIndex + 1;
    }

    if (statusText && nameText) {
        statusText.x = avatar.sprite.x;
        statusText.y = nameText.y - (nameText.__bubbleHeight || nameText.height || 18) - 5;
        statusText.zIndex = avatar.sprite.zIndex + 2;
    }
};

const resolveAvatarLabelVisibility = (worldContainer, avatars, localKey) => {
    const candidates = [];
    if (worldContainer.__objectPromptCollisionRect?.rect) {
        candidates.push({
            display: null,
            rect: worldContainer.__objectPromptCollisionRect.rect,
            priority: worldContainer.__objectPromptCollisionRect.priority || 95,
            type: "object-prompt",
            id: "nearest-object",
        });
    }

    Object.entries(avatars).forEach(([id, avatar]) => {
        if (!avatar?.sprite || avatar.sprite.destroyed) return;
        const isLocal = id === localKey;

        if (avatar.statusText) {
            candidates.push({
                display: avatar.statusText,
                rect: labelRect(avatar.statusText, 4),
                priority: isLocal ? 100 : 70,
                type: "avatar-status",
                id,
            });
        }

        if (avatar.nameText) {
            candidates.push({
                display: avatar.nameText,
                rect: labelRect(avatar.nameText, 3),
                priority: isLocal ? 90 : 50,
                type: "avatar-name",
                id,
            });
        }
    });

    const accepted = [];
    candidates
        .filter((item) => item.rect)
        .sort((a, b) => b.priority - a.priority)
        .forEach((item) => {
            const collides = accepted.some((acceptedItem) =>
                acceptedItem.id !== item.id && rectsOverlap(item.rect, acceptedItem.rect)
            );
            if (item.display) item.display.visible = !collides;
            if (!collides) accepted.push(item);
        });

    worldContainer.__labelCollisionRects = accepted.map(({rect, priority, type, id}) => ({
        rect,
        priority,
        type,
        id,
    }));
};

const updateAvatarStatusBubble = (worldContainer, avatar, status) => {
    const nextText = isActiveAvatarStatus(status) ? status.label : null;

    if (!nextText) {
        if (avatar.statusText) {
            worldContainer.removeChild(avatar.statusText);
            avatar.statusText.destroy({children: true});
            avatar.statusText = null;
        }
        return;
    }

    if (!avatar.statusText || avatar.statusText.__statusText !== nextText) {
        if (avatar.statusText) {
            worldContainer.removeChild(avatar.statusText);
            avatar.statusText.destroy({children: true});
        }
        avatar.statusText = createAvatarStatusBubble(nextText);
        applyAvatarCounterScale(avatar.statusText, avatar.counterScale);
        worldContainer.addChild(avatar.statusText);
    }
    positionAvatarLabels(avatar);
};

const destroyPixiApp = (app) => {
    if (!app) return;
    try {
        app.ticker?.stop();
        app.destroy(true, {
            children: true,
            texture: false,
            textureSource: false,
        });
    } catch (error) {
        console.warn("Pixi cleanup skipped:", error);
    }
};

const VirtualSpacePixi = ({user, onOpenActivity, activityPanelOpen = false}) => {
    const pixiContainer = useRef(null);
    const [currentRoom, setCurrentRoom] = useState(() =>
        roomFileName(readStoredAvatarPosition(user, "activity-start")?.room
            || readStoredAvatarPosition(user, "position")?.room
            || DEFAULT_ROOM_NAME)
    );
    const [roomData, setRoomData] = useState(null);
    const localUserRef = useRef(null);
    const currentRoomRef = useRef(currentRoom);
    const activityPanelOpenRef = useRef(activityPanelOpen);
    const onOpenActivityRef = useRef(onOpenActivity);
    const tableOccupancyRef = useRef({});
    const liveTableOccupancyRef = useRef({});
    const computerOccupancyRef = useRef({});
    const liveComputerOccupancyRef = useRef({});
    // console.log("User", user);

    const setVirtualMovementKey = useCallback((key, pressed) => {
        if (!window.__gs_keys) window.__gs_keys = {};
        if (window.__virtualActivityModalOpen && pressed) {
            clearGlobalMovementKeys();
            return;
        }
        window.__gs_keys[key] = pressed;
    }, []);

    const movementButtonProps = useCallback((key) => ({
        onPointerDown: (event) => {
            event.preventDefault();
            event.stopPropagation();
            event.currentTarget.setPointerCapture?.(event.pointerId);
            setVirtualMovementKey(key, true);
        },
        onPointerUp: (event) => {
            event.preventDefault();
            event.stopPropagation();
            event.currentTarget.releasePointerCapture?.(event.pointerId);
            setVirtualMovementKey(key, false);
        },
        onPointerCancel: (event) => {
            event.preventDefault();
            event.stopPropagation();
            setVirtualMovementKey(key, false);
        },
        onPointerLeave: (event) => {
            event.preventDefault();
            event.stopPropagation();
            setVirtualMovementKey(key, false);
        },
        onContextMenu: (event) => event.preventDefault(),
    }), [setVirtualMovementKey]);

    const triggerVirtualSpaceKey = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        if (window.__virtualActivityModalOpen) return;

        const keydownEvent = new KeyboardEvent("keydown", {
            key: " ",
            code: "Space",
            bubbles: true,
            cancelable: true,
        });
        const keyupEvent = new KeyboardEvent("keyup", {
            key: " ",
            code: "Space",
            bubbles: true,
            cancelable: true,
        });

        window.dispatchEvent(keydownEvent);
        window.setTimeout(() => window.dispatchEvent(keyupEvent), 80);
    }, []);

    useEffect(() => {
        currentRoomRef.current = currentRoom;
    }, [currentRoom]);

    useEffect(() => {
        activityPanelOpenRef.current = activityPanelOpen;
        window.__virtualActivityModalOpen = activityPanelOpen;

        return () => {
            window.__virtualActivityModalOpen = false;
        };
    }, [activityPanelOpen]);

    useEffect(() => {
        onOpenActivityRef.current = onOpenActivity;
    }, [onOpenActivity]);

    useEffect(() => {
        if (!roomData || !user?.course_id) {
            tableOccupancyRef.current = {};
            computerOccupancyRef.current = {};
            return undefined;
        }

        const groupIds = tableGroupIdsFromRoom(roomData);
        const computerObjectIds = computerObjectIdsFromRoom(roomData);
        if (groupIds.length === 0 && computerObjectIds.length === 0) {
            tableOccupancyRef.current = {};
            computerOccupancyRef.current = {};
            return undefined;
        }

        let active = true;
        const loadOccupancy = async () => {
            try {
                const [tableData, computerData] = await Promise.all([
                    groupIds.length > 0
                        ? apiGet(`/table/occupancy?groups=${groupIds.join(",")}`)
                        : Promise.resolve({occupancy: []}),
                    computerObjectIds.length > 0
                        ? apiGet(`/individual/occupancy?objects=${computerObjectIds.map(encodeURIComponent).join(",")}`)
                        : Promise.resolve({occupancy: []}),
                ]);
                if (!active) return;

                tableOccupancyRef.current = (tableData.occupancy || []).reduce((map, item) => {
                    const occupiedObjectIds = Array.isArray(item.occupied_object_ids) && item.occupied_object_ids.length > 0
                        ? item.occupied_object_ids
                        : [item.object_id];
                    occupiedObjectIds.forEach((occupiedObjectId) => {
                        const key = normalizeTableObjectKey(occupiedObjectId);
                        if (key) {
                            map[key] = {
                                ...item,
                                object_id: occupiedObjectId,
                                is_occupied: !!item.is_occupied,
                                is_member: !!item.is_member,
                                source: "database",
                            };
                        }
                    });
                    return map;
                }, {});
                computerOccupancyRef.current = (computerData.occupancy || []).reduce((map, item) => {
                    const key = normalizeTableObjectKey(item.object_id);
                    if (key) {
                        map[key] = {
                            ...item,
                            is_occupied: !!item.is_occupied,
                            is_owner: !!item.is_owner,
                            source: "database",
                        };
                    }
                    return map;
                }, {});
            } catch (error) {
                if (active) console.warn("Unable to load object occupancy:", error);
            }
        };

        loadOccupancy();
        const intervalId = window.setInterval(loadOccupancy, 5000);

        return () => {
            active = false;
            window.clearInterval(intervalId);
        };
    }, [roomData, user?.course_id, user?.course_group_id, user?.user_id, user?.id]);

    // Handle room switching
    const handleRoomChange = useCallback(async (newRoom) => {
        //console.log("🏠 Switching to:", newRoom);
        const nextRoom = normalizeRoomName(newRoom);
        const activeRoom = normalizeRoomName(localUserRef.current?.room || currentRoomRef.current || DEFAULT_ROOM_NAME);
        if (activeRoom === nextRoom) return;

        const nextRoomFile = roomFileName(nextRoom);
        let nextRoomData = null;

        window.__gs_roomTransitioning = true;
        clearGlobalMovementKeys();

        try {
            nextRoomData = await fetchRoomData(nextRoom);
        } catch (error) {
            window.__gs_roomTransitioning = false;
            console.error("Failed to switch room:", error);
            return;
        }

        // Update local user room info
        if (localUserRef.current) {
            const spawnPosition = randomSpawnPosition({
                mapWidth: nextRoomData?.width * nextRoomData?.tilewidth,
                mapHeight: nextRoomData?.height * nextRoomData?.tileheight,
                tileSize: nextRoomData?.tilewidth,
                room: nextRoom,
            });
            localUserRef.current.room = nextRoom;
            localUserRef.current.course_id = user.course_id;
            localUserRef.current.course_group_id = user.course_group_id || null;
            localUserRef.current.x = spawnPosition.x;
            localUserRef.current.y = spawnPosition.y;
            saveStoredAvatarPosition(user, localUserRef.current);
            clearStoredActivityStartPosition(user);
        }

        // Update displayed room and notify server
        currentRoomRef.current = nextRoomFile;
        setCurrentRoom(currentRoomRef.current);
        setRoomData(nextRoomData);
        socket.emit("join_room", {
            user: localUserRef.current,
            room: nextRoom,
        });
    }, [user]);

    const initAvatars = useCallback((app, worldContainer, checkCollision, TILE_SIZE, mapWidth, mapHeight, user, zoomFactor, localUserRef) => {
        const localKeyRef = {current: null};
        const avatars = {};
        window.__avatars = avatars;

        // Inisialisasi localUser dengan user_id agar server mengenali dengan benar
        const storedPosition = readStoredAvatarPosition(user, "activity-start")
            || readStoredAvatarPosition(user, "position");
        const activeRoom = normalizeRoomName(currentRoomRef.current || DEFAULT_ROOM_NAME);
        const storedRoom = normalizeRoomName(storedPosition?.room || DEFAULT_ROOM_NAME);
        const restoredPosition = storedRoom === activeRoom
            && !storedPosition?.isLegacyDefaultRoom
            && isPositionInsideMap(storedPosition, mapWidth, mapHeight, TILE_SIZE)
            ? storedPosition
            : null;
        const spawnPosition = restoredPosition || randomSpawnPosition({
            mapWidth,
            mapHeight,
            tileSize: TILE_SIZE,
            room: activeRoom,
        });
        const localUser = {
            user_id: user.user_id || user.id,
            avatar: avatarPublicPath(user),
            x: spawnPosition.x,
            y: spawnPosition.y,
            name: user.name || "User",
            course_id: user.course_id,
            course_group_id: user.course_group_id || null,
            direction: restoredPosition?.direction || localStorage.getItem("lastDirection") || "right",
            room: activeRoom,
        };
        localUserRef.current = localUser;

        // Setelah localUserRef.current = localUser;
        const delayedJoinTimer = window.setTimeout(() => {
            const activeRoom = normalizeRoomName(localUserRef.current.room || DEFAULT_ROOM_NAME);
            localUserRef.current.room = activeRoom;
            socket.emit("join_room", {user: localUserRef.current, room: activeRoom});
            //console.log("➡️ Joining room (delayed):", activeRoom);
        }, 200);

        // Update users state saat menerima update dari server
        socket.on("update_users", async (usersData) => {
            //console.log("🔁 Received update_users for all rooms:", usersData);
            let myKey = null;
            const currentRoom = normalizeRoomName(localUserRef.current?.room || DEFAULT_ROOM_NAME);
            const filteredUsers = Object.fromEntries(
                Object.entries(usersData).filter(([_, u]) => normalizeRoomName(u.room) === currentRoom)
            );
            liveTableOccupancyRef.current = buildLiveTableOccupancy(filteredUsers, user);
            liveComputerOccupancyRef.current = buildLiveComputerOccupancy(filteredUsers, user);
            //console.log("🏠 Filtered users for room:", currentRoom, filteredUsers);

            for (const [id, u] of Object.entries(filteredUsers)) {
                //console.log(`🔍 Checking user key=${id}`, u);
                if (
                    u.user_id === localUser.user_id ||
                    id === localUser.user_id ||
                    (u.email && u.email === user.email)
                ) {
                    //console.log("✅ Match found for local user:", id);
                    myKey = id;
                    break;
                }
            }

            if (!myKey) {
                console.warn("⚠️ Local user key not found in usersData, attempting fallback match...");
                myKey = Object.keys(filteredUsers).find(
                    (id) => filteredUsers[id].name === localUser.name
                );
                if (myKey) {
                    console.warn("⚠️ Fallback key used:", myKey);
                } else {
                    console.error("❌ No matching user found even with fallback!");
                }
            }

            if (myKey) {
                localKeyRef.current = myKey;
                if (filteredUsers[myKey]) {
                    // Do NOT overwrite local x/y — only update room
                    localUser.room = normalizeRoomName(filteredUsers[myKey].room || currentRoom);
                    localUser.activity_status = filteredUsers[myKey].activity_status || null;
                    localUserRef.current = localUser;
                }
            }

            await renderUsers(worldContainer, avatars, filteredUsers, localKeyRef, TILE_SIZE, 1 / zoomFactor);
        });

        // Tangani user yang keluar (logout/disconnect)
        socket.on("user_left", (userId) => {
            //console.log(`🧹 User left: ${userId}`);
            if (avatars[userId]) {
                worldContainer.removeChild(avatars[userId]);
                delete avatars[userId];
            }
        });

        // Pindahkan logika movement dan camera follow ke modul terpisah
        const cleanupMovement = initAvatarMovement(app, worldContainer, avatars, localUserRef, localKeyRef, checkCollision, TILE_SIZE, mapWidth, mapHeight, zoomFactor, user);

        return {
            localUserRef,
            localKeyRef,
            cleanup: () => {
                window.clearTimeout(delayedJoinTimer);
                cleanupMovement?.();
            },
        };
    }, []);
    // Fetch current room data
    useEffect(() => {
        // Fetch room JSON from public folder
        let active = true;

        fetchRoomData(currentRoom)
            .then((data) => {
                if (active) setRoomData(data);
            })
            .catch((error) => {
                if (active) console.error("Failed to load room data:", error);
            });

        return () => {
            active = false;
        };
    }, [currentRoom]);

    useEffect(() => {
        if (!roomData) return; // Do not run if roomData not loaded yet

        let cleanupPixi = null;
        let cancelled = false;

        (async () => {
            // Pixel-art friendly rendering: avoid texture bleeding / seams when zooming
            //PIXI.TextureStyle.defaultOptions.scaleMode = 'nearest'; // untuk zoom 1.5 tapi kualitas render kurang baik
            const app = new PIXI.Application();
            // We'll get mapWidth, mapHeight, TILE_SIZE from initMap
            // But we need to set the app size initially to something reasonable (will be resized below)
            await app.init({
                width: 1280,
                height: 720,
                backgroundColor: 0xf0f0f0,
                antialias: false,
                roundPixels: true,
            });
            // Round rendering to whole pixels to prevent grey seams between tiles at non-integer zoom
            const container = pixiContainer.current;
            if (!container) return;
            if (cancelled) {
                destroyPixiApp(app);
                return;
            }
            container.innerHTML = "";
            container.appendChild(app.canvas);
            app.canvas.style.display = "block";
            app.canvas.style.margin = "0";

            // Init Map
            const {worldContainer, checkCollision, TILE_SIZE, mapWidth, mapHeight} = await initMap(app, roomData);
            if (cancelled) {
                destroyPixiApp(app);
                return;
            }
            app.stage.addChild(worldContainer);

            // Zoom factor
            const zoomFactor = visualScaleForRoom(currentRoom);
            worldContainer.scale.set(zoomFactor);

            const resizeToContainer = () => {
                const width = Math.max(320, container.clientWidth || window.innerWidth);
                const height = Math.max(320, container.clientHeight || window.innerHeight);
                app.renderer.resize(width, height);
                // IMPORTANT: keep container aligned to whole pixels to avoid tile seams while zoomed
                worldContainer.x = Math.round((app.renderer.width - mapWidth * zoomFactor) / 2);
                worldContainer.y = Math.round((app.renderer.height - mapHeight * zoomFactor) / 2);
            };
            resizeToContainer();
            window.addEventListener("resize", resizeToContainer);
            //================================================================================================================================================

            // Avatars
            const avatarRuntime = initAvatars(app, worldContainer, checkCollision, TILE_SIZE, mapWidth, mapHeight, user, zoomFactor, localUserRef);
            if (cancelled) {
                avatarRuntime?.cleanup?.();
                destroyPixiApp(app);
                return;
            }

            //============================================================================================================
            // Smooth ticker updates for avatar movement
            const smoothAvatarTicker = () => {
                if (cancelled || !app.renderer || worldContainer.destroyed) return;
                if (!window.__avatars) return;

                for (const id in window.__avatars) {
                    const a = window.__avatars[id];
                    if (!a?.sprite || a.sprite.destroyed) continue;

                    // Only apply smoothing if targetX/targetY are numeric (remote avatars)
                    if (typeof a.targetX === "number" && typeof a.targetY === "number") {
                        // Optional: use velocity if available
                        if (typeof a.vx === "number" && typeof a.vy === "number") {
                            a.sprite.x += a.vx * 0.5;
                            a.sprite.y += a.vy * 0.5;
                        }
                        // Soft correction toward the target
                        a.sprite.x += (a.targetX - a.sprite.x) * 0.05;
                        a.sprite.y += (a.targetY - a.sprite.y) * 0.05;
                    }

                    // Keep depth sorting correct for moving avatars (sort by feet Y)
                    a.sprite.zIndex = a.sprite.y;

                    positionAvatarLabels(a);
                }
                resolveAvatarLabelVisibility(worldContainer, window.__avatars, avatarRuntime?.localKeyRef?.current);
            };
            app.ticker.add(smoothAvatarTicker);

            // Objects
            //console.log("🧭 Calling initObjects...");
            const cleanupObjects = initObjects(app, worldContainer, roomData, user, localUserRef, 1, handleRoomChange, {
                promptScale: 1 / zoomFactor,
                onOpenActivity: (launch) => {
                    const localUser = localUserRef.current;
                    if (localUser) {
                        const position = {
                            x: localUser.x,
                            y: localUser.y,
                            room: localUser.room || currentRoomRef.current,
                            direction: localUser.direction || "right",
                        };
                        saveStoredAvatarPosition(user, position, "activity-start");
                        saveStoredAvatarPosition(user, position, "position");
                    }
                    return onOpenActivityRef.current?.(launch);
                },
                isInteractionDisabled: () => activityPanelOpenRef.current,
                getTableOccupancy: ({objectId}) => {
                    const key = normalizeTableObjectKey(objectId);
                    if (!key) return null;

                    const live = liveTableOccupancyRef.current[key] || null;
                    const database = tableOccupancyRef.current[key] || null;

                    if (live?.is_member || database?.is_member) {
                        return {
                            ...(database || live || {}),
                            is_member: true,
                            is_occupied: false,
                        };
                    }

                    return live || database || null;
                },
                getComputerOccupancy: ({objectId}) => {
                    const key = normalizeTableObjectKey(objectId);
                    if (!key) return null;

                    const live = liveComputerOccupancyRef.current[key] || null;
                    const database = computerOccupancyRef.current[key] || null;

                    if (live?.is_owner || database?.is_owner) {
                        return {
                            ...(database || live || {}),
                            is_owner: true,
                            is_occupied: false,
                        };
                    }

                    return live || database || null;
                },
            });
            if (cancelled) {
                app.ticker.remove(smoothAvatarTicker);
                avatarRuntime?.cleanup?.();
                cleanupObjects?.();
                destroyPixiApp(app);
                return;
            }
            socket.emit("request_update_users", {
                course_id: user.course_id,
                room: normalizeRoomName(localUserRef.current?.room || currentRoomRef.current || DEFAULT_ROOM_NAME),
            });
            window.__gs_roomTransitioning = false;

            cleanupPixi = () => {
                app.ticker.remove(smoothAvatarTicker);
                avatarRuntime?.cleanup?.();
                cleanupObjects?.();
                window.removeEventListener("resize", resizeToContainer);
                socket.off("update_users");
                socket.off("user_moved");
                socket.off("user_left");
                if (window.__avatars) window.__avatars = null;
                destroyPixiApp(app);
            };
        })();

        return () => {
            cancelled = true;
            clearGlobalMovementKeys();
            if (cleanupPixi) cleanupPixi();
        };
    }, [currentRoom, handleRoomChange, initAvatars, roomData, user]);

    if (!roomData) {
        return null; // or loading indicator if desired
    }

    return (
        <div
            className="virtual-pixi-frame"
            style={{
                width: "100%",
                height: "100%",
                background: "#dcdcdc",
                overflow: "hidden",
            }}
        >
            <div ref={pixiContainer} className="virtual-pixi-canvas-host" />
            <div className="virtual-touch-controls" aria-label="Kontrol ruang virtual">
                <div className="virtual-touch-dpad" aria-label="Kontrol arah">
                    <button
                        type="button"
                        className="virtual-touch-button virtual-touch-button--up"
                        aria-label="Jalan ke atas"
                        {...movementButtonProps("ArrowUp")}
                    >
                        ↑
                    </button>
                    <button
                        type="button"
                        className="virtual-touch-button virtual-touch-button--left"
                        aria-label="Jalan ke kiri"
                        {...movementButtonProps("ArrowLeft")}
                    >
                        ←
                    </button>
                    <button
                        type="button"
                        className="virtual-touch-button virtual-touch-button--right"
                        aria-label="Jalan ke kanan"
                        {...movementButtonProps("ArrowRight")}
                    >
                        →
                    </button>
                    <button
                        type="button"
                        className="virtual-touch-button virtual-touch-button--down"
                        aria-label="Jalan ke bawah"
                        {...movementButtonProps("ArrowDown")}
                    >
                        ↓
                    </button>
                </div>
                <button
                    type="button"
                    className="virtual-touch-action"
                    aria-label="Tekan spasi"
                    onPointerDown={triggerVirtualSpaceKey}
                    onContextMenu={(event) => event.preventDefault()}
                >
                    Spasi
                </button>
            </div>
        </div>
    );
};

export default VirtualSpacePixi;

export async function renderUsers(worldContainer, avatars, usersData, localKeyRef, TILE_SIZE, avatarCounterScale = 1) {
    // Hapus avatar yang sudah tidak ada di usersData
    Object.keys(avatars).forEach((id) => {
        if (!usersData[id]) {
            if (avatars[id].sprite) {
                worldContainer.removeChild(avatars[id].sprite);
            }
            if (avatars[id].nameText) {
                worldContainer.removeChild(avatars[id].nameText);
            }
            if (avatars[id].statusText) {
                worldContainer.removeChild(avatars[id].statusText);
            }
            delete avatars[id];
        }
    });

    for (const [id, u] of Object.entries(usersData)) {
        const avatarPath = avatarPublicPath(u);

        // Jika avatar untuk user ini belum ada → buat sprite & nameText
        if (!avatars[id]) {
            let sprite;

            try {
                const animation = await loadAvatarAnimation(avatarPath);
                const initialDirection = u.direction || "right";
                const initialFrames = animation.frames[initialDirection] || animation.frames.right;

                sprite = new PIXI.AnimatedSprite(initialFrames);
                sprite.animationSpeed = 0.13;
                sprite.gotoAndStop(0);

                // Ukuran Avatar
                const targetAvatarWidth = animation.mode === "directional-sheet"
                    ? TILE_SIZE * AVATAR_SHEET_RENDER_TILE_WIDTH
                    : TILE_SIZE * 3;
                const scaleFactor = targetAvatarWidth / sprite.texture.width;
                sprite.scale.set(scaleFactor * avatarCounterScale);

// Top-down: x/y represents FEET so the head can go behind walls
                sprite.anchor.set(0.5, 1);

// Save base scale for left/right flipping (always positive)
                sprite.baseScaleX = Math.abs(sprite.scale.x);

// zIndex will be set after positioning (sort by feet Y)

                // SET POSISI AWAL LANGSUNG KE POSISI USER (feet position)
                sprite.x = u.x;
                sprite.y = u.y;

// Depth sorting by feet Y
                sprite.zIndex = sprite.y;

                worldContainer.addChild(sprite);

                const nameText = createAvatarNameTag(u.name || "User");
                applyAvatarCounterScale(nameText, avatarCounterScale);

                nameText.zIndex = sprite.zIndex + 1;
                nameText.x = sprite.x;
                nameText.y = sprite.y - sprite.height * NAME_OFFSET_MULT - NAME_OFFSET_PX;
                worldContainer.addChild(nameText);

                // Simpan avatar + state tambahan
                const isLocal = id === localKeyRef.current;
                avatars[id] = {
                    sprite,
                    nameText,
                    statusText: null,
                    counterScale: avatarCounterScale,
                    framesByDirection: animation.frames,
                    animationMode: animation.mode,
                    // Only remote avatars get targetX/targetY for smoothing
                    ...(isLocal ? {} : {targetX: u.x, targetY: u.y}),
                    lastDirection: u.direction || "right",
                };
                setAvatarDirection(avatars[id], u.direction || "right");
                updateAvatarStatusBubble(worldContainer, avatars[id], u.activity_status);
            } catch (err) {
                console.error("Failed to load avatar:", avatarPath, err);
                continue;
            }
        } else {
            // Avatar sudah ada → update posisi & arah
            const avatar = avatars[id];

            // 1) UPDATE targetX/targetY untuk diinterpolasi di ticker (remote only)
            if (id !== localKeyRef.current) {
                avatar.targetX = u.x;
                avatar.targetY = u.y;
            }
            updateAvatarStatusBubble(worldContainer, avatar, u.activity_status);

            // 2) UPDATE ARAH SPRITE BERDASARKAN direction USER
            let dir = u.direction;
            if (!dir && avatar.lastDirection) {
                dir = avatar.lastDirection;
            }

            if (id === localKeyRef.current) {
                // Local user: kalau server belum sync, pakai lastDirection di localStorage
                dir = dir || localStorage.getItem("lastDirection") || "right";
            }

            setAvatarDirection(avatar, dir);

            avatar.lastDirection = dir;

            // Remote avatars: play walk animation only if moving
            if (id !== localKeyRef.current) {
                const deltaX = Math.abs(avatar.targetX - avatar.sprite.x);
                const deltaY = Math.abs(avatar.targetY - avatar.sprite.y);
                const moving = deltaX > 0.5 || deltaY > 0.5;

                if (moving) {
                    if (avatar.sprite.play) avatar.sprite.play();
                } else {
                    if (avatar.sprite.gotoAndStop) avatar.sprite.gotoAndStop(0);
                }
            }
        }
    }

    // Tetap pakai sorting by zIndex
    worldContainer.sortableChildren = true;
    resolveAvatarLabelVisibility(worldContainer, avatars, localKeyRef.current);
}

export function initAvatarMovement(app, worldContainer, avatars, localUserRef, localKeyRef, checkCollision, TILE_SIZE, mapWidth, mapHeight, zoomFactor, user) {
    const step = 2;
    let lastDirection = localUserRef.current?.direction || "right";
    if (!window.__gs_keys) window.__gs_keys = {};
    const keys = window.__gs_keys;
    const clearMovementKeys = () => {
        keys.ArrowUp = false;
        keys.ArrowDown = false;
        keys.ArrowLeft = false;
        keys.ArrowRight = false;
    };

    // Hindari multi-attach ketika initAvatarMovement dipanggil berkali-kali
    if (!window.__gs_keysBound) {
        window.addEventListener("keydown", (e) => {
            if (window.__virtualActivityModalOpen) {
                clearMovementKeys();
                return;
            }
            const target = e.target;
            const isTyping = target && (
                target.tagName === "INPUT" ||
                target.tagName === "TEXTAREA" ||
                target.tagName === "SELECT" ||
                target.isContentEditable
            );
            if (isTyping) return;
            // Cegah halaman scroll saat pakai Arrow keys
            if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " "].includes(e.key)) e.preventDefault();
            window.__gs_keys[e.key] = true;
        }, {passive: false});
        window.addEventListener("keyup", (e) => {
            window.__gs_keys[e.key] = false;
        }, {passive: true});
        window.__gs_keysBound = true;
    }

    const movementTicker = () => {
        // >>> Perbaikan utama: ambil localUser sebelum dipakai
        if (!app.renderer || worldContainer.destroyed) return;
        if (window.__gs_roomTransitioning) {
            clearMovementKeys();
            return;
        }
        if (window.__virtualActivityModalOpen) {
            clearMovementKeys();
            const myKey = localKeyRef.current;
            if (myKey && avatars[myKey]?.sprite && !avatars[myKey].sprite.destroyed) {
                avatars[myKey].sprite.gotoAndStop?.(0);
            }
            return;
        }
        const localUser = localUserRef.current;
        if (!localUser) return;
        let moved = false;
        if (keys["ArrowUp"]) {
            moved = true;
        }
        if (keys["ArrowDown"]) {
            moved = true;
        }
        if (keys["ArrowLeft"]) {
            moved = true;
        }
        if (keys["ArrowRight"]) {
            moved = true;
        }
        if (moved) {
            let newX = localUser.x;
            let newY = localUser.y;
            // Persist last known direction
            let direction = lastDirection || "right";

            // Frame-rate independent movement
            // Clamp dt to avoid stutter on slower devices
            const dt = app.ticker.deltaMS / 16.67;   // Normalize to 60fps
            const clampedDt = Math.min(dt, 1.2);
            const scaledStep = step * clampedDt;

            if (keys["ArrowUp"]) {
                newY -= scaledStep;
                direction = "up";
            }
            if (keys["ArrowDown"]) {
                newY += scaledStep;
                direction = "down";
            }
            if (keys["ArrowLeft"]) {
                newX -= scaledStep;
                direction = "left";
            }
            if (keys["ArrowRight"]) {
                newX += scaledStep;
                direction = "right";
            }

            // Prevent movement into collision tiles (feet collider)
            // Use axis-separated resolution so bumping horizontally doesn't let part of the sprite clip into walls.
            const rx = TILE_SIZE * 0.9; // horizontal shoe radius (slightly larger than before)
            const ry = TILE_SIZE * 0.25; // vertical shoe radius

            const isBlockedAt = (x, y) => {
                // Sample a small set of points around the feet area.
                // Extra diagonal points prevent left/right "partial" clipping when sliding along a wall.
                return (
                    checkCollision(x, y) ||
                    checkCollision(x - rx, y) ||
                    checkCollision(x + rx, y) ||
                    checkCollision(x, y - ry) ||
                    checkCollision(x - rx, y - ry) ||
                    checkCollision(x + rx, y - ry)
                );
            };

            // Resolve X then Y (allows sliding along walls)
            let nextX = localUser.x;
            let nextY = localUser.y;

            if (!isBlockedAt(newX, localUser.y)) {
                nextX = newX;
            }
            if (!isBlockedAt(nextX, newY)) {
                nextY = newY;
            }

            localUser.x = nextX;
            localUser.y = nextY;

            // Clamp to map boundaries
            const margin = TILE_SIZE * 2;
            localUser.x = Math.max(margin, Math.min(localUser.x, mapWidth - margin));
            localUser.y = Math.max(margin, Math.min(localUser.y, mapHeight - margin));

            // Update avatar position visually
            const myKey = localKeyRef.current;
            if (myKey && avatars[myKey]?.sprite && !avatars[myKey].sprite.destroyed) {

                avatars[myKey].sprite.x = localUser.x;
                avatars[myKey].sprite.y = localUser.y;
                // Depth sorting by feet Y
                avatars[myKey].sprite.zIndex = avatars[myKey].sprite.y;

                if (avatars[myKey].nameText && !avatars[myKey].nameText.destroyed) {
                    positionAvatarLabels(avatars[myKey]);
                }

                setAvatarDirection(avatars[myKey], direction);

                const isMovingNow =
                    keys["ArrowUp"] || keys["ArrowDown"] || keys["ArrowLeft"] || keys["ArrowRight"];

                if (isMovingNow) {
                    if (avatars[myKey].sprite.play) avatars[myKey].sprite.play();
                } else {
                    if (avatars[myKey].sprite.gotoAndStop) avatars[myKey].sprite.gotoAndStop(0);
                }
            }
            // Simpan arah terakhir di memori dan localStorage untuk persistensi antar-room
            localUser.direction = direction;
            lastDirection = direction;
            localStorage.setItem("lastDirection", direction);
            saveStoredAvatarPosition(user, {
                x: localUser.x,
                y: localUser.y,
                room: localUser.room || DEFAULT_ROOM_NAME,
                direction,
            });
            clearStoredActivityStartPosition(user);
            socket.emit("move", {
                id: localKeyRef.current || localUser.user_id,
                user_id: localUser.user_id,
                x: localUser.x,
                y: localUser.y,
                direction,
            });
        } else {
            // No movement → force idle animation for local avatar
            const myKey = localKeyRef.current;
            if (myKey && avatars[myKey]?.sprite && !avatars[myKey].sprite.destroyed) {
                if (avatars[myKey].sprite.gotoAndStop) {
                    avatars[myKey].sprite.gotoAndStop(0);
                }
            }
        }

        // Kamera mengikuti avatar (camera follow)
        const viewWidth = app.renderer.width;
        const viewHeight = app.renderer.height;
        const targetX = -localUser.x * zoomFactor + viewWidth / 2;
        const targetY = -localUser.y * zoomFactor + viewHeight / 2;

        const scaledMapWidth = mapWidth * zoomFactor;
        const scaledMapHeight = mapHeight * zoomFactor;

        // Keep smaller maps centered; otherwise clamp camera so it never shows past map bounds.
        worldContainer.x = Math.round(
            scaledMapWidth <= viewWidth
                ? (viewWidth - scaledMapWidth) / 2
                : Math.min(0, Math.max(-scaledMapWidth + viewWidth, targetX))
        );
        worldContainer.y = Math.round(
            scaledMapHeight <= viewHeight
                ? (viewHeight - scaledMapHeight) / 2
                : Math.min(0, Math.max(-scaledMapHeight + viewHeight, targetY))
        );
    };
    app.ticker.add(movementTicker);
    return () => app.ticker.remove(movementTicker);

}
